(ns app.front.view.complaint.add-form
  (:require [app.domain.attachment.model :as attachment-model]
            [app.domain.complaint.model :as complaint-model]
            [app.domain.file.model :as file-model]
            [app.domain.form.validators :as form-validators]
            [app.domain.group.config.model :as config-model]
            [app.domain.util :as du]
            [app.front.file.upload :as upload]
            [app.front.ui :as ui]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.select :as ui-select]
            [app.front.ui.separator :as ui-sep]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.complaint.util :as complaint-util]
            [app.i18n.model :as i18n-model]
            [app.rich-text :as rich-text]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.http :as core.http]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.utils]))


(rf/reg-event-fx ::submit-new-complaint
  (fn [{:keys [db]} [_ command signed-off authority-group-id municipality-number property coordinates location complaint on-success-evt]]
    ;; The form can be cleared and a new draft started before the response arrives, so the
    ;; response only touches the form state of its own submission.
    (let [submission-id (random-uuid)]
      {:db           (update db ::data assoc ::create-in-progress? true ::submission-id submission-id)
       :comm/command {:command          command
                      :data             (merge (select-keys property [:gis-id :gis-layer])
                                               (p/assoc-if {:municipality/number municipality-number
                                                            :location            (let [[x y] coordinates]
                                                                                   (-> (dissoc location :address)
                                                                                       (assoc :location.center/x x
                                                                                              :location.center/y y)))
                                                            :complaint           complaint
                                                            :signed-off          signed-off
                                                            :authority-group-id  authority-group-id}
                                                           :address (:address location)))
                      :on-success       [::submit-new-complaint.success on-success-evt submission-id]
                      :on-error         [::submit-new-complaint.failed submission-id]
                      :finally-dispatch [::submit-new-complaint.done submission-id]}})))


(defn- current-submission? [db submission-id]
  (= submission-id (get-in db [::data ::submission-id])))


(defn- end-submission [db submission-id]
  (cond-> db
    (current-submission? db submission-id) (update ::data dissoc ::create-in-progress? ::submission-id)))


(rf/reg-event-fx ::submit-new-complaint.success
  (fn [{:keys [db]} [_ on-success-evt submission-id _ complaint]]
    {:db (cond-> db
           (current-submission? db submission-id)
           (update ::data dissoc ::new-complaint-data :create-form-open? ::signed-off ::selected-complaint-category))
     :fx [[:dispatch [on-success-evt complaint]]]}))


(rf/reg-event-db ::submit-new-complaint.done
  (fn [db [_ submission-id]]
    (end-submission db submission-id)))


(rf/reg-event-fx ::submit-new-complaint.failed
  (fn [{:keys [db]} [_ submission-id]]
    ;; Network failures skip :finally-dispatch, and :on-error replaces the default notification.
    {:db (end-submission db submission-id)
     :fx [[:dispatch [:notifications/add :toast :error
                      :error/connection-error-title
                      :error/connection-error-description]]]}))


(rf/reg-event-db ::set-new-complaint-data
  (fn [db [_ k v]]
    (if (or (nil? v)
            (and (string? v) (str/blank? v)))
      (p/dissoc-in db [::data ::new-complaint-data k])
      (assoc-in db [::data ::new-complaint-data k] v))))


(rf/reg-event-db ::start-create-complaint
  (fn [db [_ location]]
    (-> db
        (assoc-in [::data ::new-complaint-data] (p/assoc-if {} :complaint/description-of-location (:location/description location)))
        (assoc-in [::data :create-form-open?] true))))


(rf/reg-event-db ::clear-data
  (fn [db _]
    (dissoc db ::data)))


(rf/reg-sub ::data :-> ::data)


(rf/reg-sub ::create-in-progress?
  :<- [::data]
  :-> ::create-in-progress?)


(rf/reg-sub ::new-complaint-data
  :<- [::data]
  :-> ::new-complaint-data)

(rf/reg-event-db ::set-disclaimer-configs
  (fn [db [_ disclaimer-configs]]
    (assoc db ::disclaimer-configs disclaimer-configs)))

(rf/reg-sub ::disclaimer-configs
  :-> ::disclaimer-configs)


(rf/reg-sub ::permit-type-disclaimer-config
  :<- [::selected-permit-type]
  :<- [::disclaimer-configs]
  (fn [[permit-type permit-type-disclaimer-config]]
    (get permit-type-disclaimer-config permit-type)))


(rf/reg-sub ::sign-off-required?
  :<- [::permit-type-disclaimer-config]
  :<- [::ff/enabled? :feature-flag.id/complaint-sign-off-may-be-required]
  (fn [[sign-off-config sign-off-may-required?] _]
    (when sign-off-may-required?
      (:group.config/complaint-sign-off-required? sign-off-config))))


(rf/reg-sub ::signed-off
  :<- [::data]
  :-> ::signed-off)

(rf/reg-sub ::complaint-photos
  :<- [::new-complaint-data]
  (fn [data _]
    (-> data :complaint/photos)))


(rf/reg-sub ::complaint-photos-limit-exceeded
  :<- [::complaint-photos]
  (fn [photos _]
    (> (count photos) complaint-model/allowed-amount-of-images)))


(rf/reg-sub ::photo-required-but-missing?
  :<- [::complaint-photos]
  :<- [::selected-complaint-category]
  (fn [[photos category] _]
    (and (:group.config/require-photo-with-submission? category)
         (empty? photos))))


(rf/reg-sub ::selected-complaint-category
  :<- [::data]
  :-> ::selected-complaint-category)


(rf/reg-event-db ::toggle-signed-off
  (fn [db _]
    (update-in db [::data ::signed-off] not)))


(rf/reg-event-db ::set-complaint-category
  (fn [db [_ category]]
    (assoc-in db [::data ::selected-complaint-category] category)))

(rf/reg-sub ::selected-permit-type
  :<- [::selected-complaint-category]
  :-> :permit-type)

(rf/reg-sub ::disclaimer
  :<- [::permit-type-disclaimer-config]
  :<- [:language]
  (fn [[permit-type-disclaimer-config language] _]
    (config-model/localized-disclaimer permit-type-disclaimer-config language)))


(rf/reg-sub ::permit-type-for-filter
  :<- [::data]
  :-> ::permit-type-for-filter)

(rf/reg-event-db ::set-permit-type-for-filter
  (fn [db [_ permit-type]]
    (assoc-in db [::data ::permit-type-for-filter]
              (when-not (-> db ::data ::permit-type-for-filter (= permit-type))
                permit-type))))

(rf/reg-sub ::complaint-category-name
  :<- [::selected-complaint-category]
  :<- [:language]
  (fn [[category lang] _]
    (config-model/localized-name category lang)))


(rf/reg-sub ::create-form-open?
  :<- [::data]
  :-> :create-form-open?)


(rf/reg-event-db ::unattach-complaint-photo
  (fn [db [_ id]]
    (update-in db
               [::data ::new-complaint-data :complaint/photos]
               (fn [photos]
                 (filterv #(not= (:file-id %) id) photos)))))


(rf/reg-event-fx ::attach-complaint-photo
  (fn [{:keys [db]} [_ image]]
    (let [new-db                          (update-in db [::data ::new-complaint-data :complaint/photos] conj image)
          count-images                    (count (get-in new-db [::data ::new-complaint-data :complaint/photos]))
          ; notify when number of photos exceeds the limit by 1 photo
          ; consequently, the user receives only one notification even if the limit is exceeded by more than 1 photo
          photo-limit-exceeded-first-time (= count-images (inc complaint-model/allowed-amount-of-images))]
      {:db new-db
       :fx (when photo-limit-exceeded-first-time
             [[:dispatch [:notifications/add :toast :warning
                          :complaint/number-of-complaint-photos-limit-exceeded-warning-title
                          :complaint/number-of-complaint-photos-limit-exceeded-warning-caption]]])})))


(defn file-preview [{:keys [file-id]} tr]
  (let [format-date-fn (<sub [::date-util/date-fn])
        category-name  (<sub [::complaint-category-name])
        now            (format-date-fn (js/Date.))]
    [:div.relative.w-full.bg-white.p-2.grid.grid-cols-3
     [:div.relative.col-span-2.grid.w-100
      [:img.w-full.h-full.complaint-image-fit
       {:src (str (core.http/command-name->path :user/download-unlinked-file)
                  "?file-id=" file-id)}]
      [:div.grid.bg-ui-night-sky-100.text-white.p-4.bottom-0.col-span-2.w-100
       (or category-name (tr [:complaint/category])) ", " now]]
     [:div.flex.justify-end.mt-3.align-top.col-start-3.self-start
      (cc/button {:kind     :ghost
                  :size     :small
                  :icon     i/trash
                  :on-click #(>evt [::unattach-complaint-photo file-id])}
                 (tr [:delete]))]]))


(defn image-attachment-section [tr]
  (let [images           (<sub [::complaint-photos])
        adding-disabled? (>= (count images) complaint-model/allowed-amount-of-images)
        photo-missing?   (<sub [::photo-required-but-missing?])]
    [:div.pt-6
     [:label.bx--label {:for "complaint-photo-attachment"}
      (tr [:complaint/photos-instruction] [complaint-model/allowed-amount-of-images])]
     [:div.flex.flex-row.flex-wrap.gap-6
      (for-frag [image images]
        [file-preview image tr])
      [upload/upload-button "complaint-photo-attachment"
       {:disabled?        adding-disabled?
        :attach-file-fn   (fn [file-id filename content-type md5]
                            (>evt [::attach-complaint-photo
                                   {:file-id file-id
                                    :file    {:file/filename     filename
                                              :file/content-type content-type
                                              :file/category     :file.category/complaint-photo
                                              :file/md5          md5}}]))
        :button-i18n-key  :complaint/upload-photos
        :button-opts      {:kind :tertiary
                           :size :field}
        :multiple         true
        :size-constraints {:bytes (file-model/size-limits :file.category/complaint-photo)}
        :accept-types     attachment-model/allowed-image-types}]]
     (when photo-missing?
       [:div.mt-2.text-danger.text-sm
        (tr [:complaint/photo-required-with-submission])])]))

(defn image-attachment-section-v2 [tr]
  (let [images           (<sub [::complaint-photos])
        adding-disabled? (>= (count images) complaint-model/allowed-amount-of-images)
        photo-missing?   (<sub [::photo-required-but-missing?])
        format-date-fn   (<sub [::date-util/date-fn])
        category-name    (or (<sub [::complaint-category-name])
                             (tr [:complaint/category]))
        now              (format-date-fn (js/Date.))]
    [:div.pt-6
     [:div.bx--label
      (tr [:complaint/photos-instruction] [complaint-model/allowed-amount-of-images])]
     [:div
      [:div.grid.grid-cols-1.lg:grid-cols-2.gap-4
       (for-frag [{:keys [file-id] {:file/keys [filename content-type]} :file} images]
         [:div {:id (str "file-preview-" file-id)}
          [:img.w-full.border.border-solid.border-ui-gray
           {:src   (str (core.http/command-name->path :user/download-unlinked-file)
                        "?file-id=" file-id)
            :alt   (str filename " (" content-type ")")
            :style {:min-height "40px"}}]
          [:div.bg-ui-night-sky-100.text-white.p-4.flex.items-center.justify-between
           [:div category-name ", " now]
           [ui/button {:kind       :secondary
                       :class-name "bg-white"
                       :size       :small
                       :icon       i/trash
                       :on-click   #(>evt [::unattach-complaint-photo file-id])}
            (tr [:delete])]]])]
      [:div.mt-4
       [upload/upload-button "complaint-photo-attachment"
        {:disabled?        adding-disabled?
         :attach-file-fn   (fn [file-id filename content-type md5]
                             (>evt [::attach-complaint-photo
                                    {:file-id file-id
                                     :file    {:file/filename     filename
                                               :file/content-type content-type
                                               :file/category     :file.category/complaint-photo
                                               :file/md5          md5}}]))
         :button-i18n-key  :complaint/upload-photos
         :button-opts      {:kind :tertiary
                            :size :field}
         :multiple         true
         :size-constraints {:bytes (file-model/size-limits :file.category/complaint-photo)}
         :accept-types     attachment-model/allowed-image-types}]]]
     (when photo-missing?
       [:div.mt-2.text-danger.text-sm
        (tr [:complaint/photo-required-with-submission])])]))


(defn invalid-value? [form-mode field-name required? value]
  (and (= form-mode :complaint/edit)
       (or (and (nil? value) required?)
           (and (some? value) (not (complaint-util/valid? field-name value))))))


(defn invalid-reason-display [tr invalid-reason]
  [:div.mt-2.text-xs.text-gray-700
   (for-frag [[k v] invalid-reason
              :let [msg (if (instance? schema.utils/ValidationError v)
                          (form-validators/string-error v)
                          (str/replace (str v) "-" " "))]]
     [:div (str (tr [k]) ": " msg)])])

(defn location-address [location tr]
  (or (get-in location [:address :address/full-address])
      (when-let [{:keys [longitude latitude]} (futil/lat-long location)]
        [:div
         [:p (tr [:location/latitude]) ": " latitude]
         [:p (tr [:location/longitude]) ": " longitude]
         [:p (:location/description location)]])))

(rf/reg-sub ::authority-group-id
  (fn [_ [_ complaint-data authority-groups]]
    (cond
      (= 1 (count authority-groups))
      (-> authority-groups first :authority-group-id)
      (:complaint/category-group-config complaint-data)
      (->> authority-groups
           (mapcat :categories)
           (filter #(= (:db/id %)
                       (:complaint/category-group-config complaint-data)))
           first
           :authority-group-id))))

(defn authority-group-id-signal [[_ complaint-data authority-groups]]
  (rf/subscribe [::authority-group-id complaint-data authority-groups]))

(rf/reg-sub ::authority-group
  authority-group-id-signal
  (fn [authority-group-id [_ _ authority-groups]]
    (p/find-first authority-groups {:authority-group-id authority-group-id})))


(rf/reg-sub ::categories
  :<- [::permit-type-for-filter]
  authority-group-id-signal
  (fn [[permit-type-for-filter authority-group-id] [_ _ authority-groups lang]]
    (->> authority-groups
         (filter (fn [ag]
                   (or (nil? authority-group-id)
                       (= (:authority-group-id ag) authority-group-id))))
         (sort-by :permit-type)
         (filter (fn [ag]
                   (or (nil? permit-type-for-filter)
                       (= (:permit-type ag) permit-type-for-filter))))
         (mapcat (fn [group]
                   (->> group
                        :categories
                        (map #(assoc % :permit-type (:permit-type group)))
                        (config-model/sort-group-configs lang)))))))

(rf/reg-sub ::form-errors
  (fn [[_ complaint-data authority-groups _coordinates _tr]]
    [(rf/subscribe [::authority-group complaint-data authority-groups])
     (rf/subscribe [::signed-off])
     (rf/subscribe [::sign-off-required?])
     (rf/subscribe [::complaint-photos-limit-exceeded])
     (rf/subscribe [::photo-required-but-missing?])])
  (fn [[authority-group signed-off? sign-off-required? photo-limit-exceeded photo-required-missing?]
       [_ complaint-data _authority-groups coordinates tr]]
    (let [complaint-checker (-> authority-group
                                :required-complaint-fields
                                ;; input maybe nil but make-checker handles that
                                complaint-model/make-ui-checker)]
      (if (empty? coordinates)
        {:entity/location "coordinates missing"}
        (cond-> (complaint-checker (or complaint-data {}))
          photo-limit-exceeded
          (conj [:complaint/photos
                 (tr [:complaint/number-of-complaint-photos-limit-exceeded-warning-caption])])

          photo-required-missing?
          (conj [:complaint/photos
                 (tr [:complaint/photo-required-with-submission])])

          (and sign-off-required? (not signed-off?))
          (conj [:group.config/complaint-sign-off-required?
                 (tr [:error/complaint-sign-off-required])]))))))


(defn category-filter [tr permit-types]
  (let [permit-type-for-filter (<sub [::permit-type-for-filter])]
    [:div.mb-2
     [:div (tr [:complaint/filter-by-department])]
     [:div.flex.flex-wrap.my-1.w-full
      (for-frag [permit-type permit-types
                 :let [selected? (= permit-type permit-type-for-filter)]]
        [:button.mr-2.mt-2.last:mr-0.rounded
         {:style    {:max-width "320px"}
          :on-click #(>evt [::set-permit-type-for-filter permit-type])
          :class    [complaint-util/category-base-style
                     (if selected?
                       complaint-util/category-selected-style
                       complaint-util/category-unselected-style)]}
         (tr [permit-type])
         (when-let [icon (ui/permit-type-icon permit-type)]
           [:div.ml-1.p-1 [:> icon
                           {:class-name "block"}]])])]]))


(defn category-selection-for-reopen [{:keys [tr lang authority-groups finalize-reopen]}]
  (let [categories (<sub [::categories nil authority-groups lang])]
    [:<>
     [:h3.font-bold.text-lg.mb-2
      (tr [:code-enforcement/edit-complaint])]
     [:div.flex.flex-wrap.mt-1.w-full
      (for-frag [category categories]
        [:button.mr-2.mt-2.last:mr-0
         {:style    {:max-width "320px"}
          :on-click #(finalize-reopen category)
          :class    [complaint-util/category-base-style complaint-util/category-unselected-style]}
         [:div.flex-1.text-left (config-model/localized-name category lang)]])]]))


(defn- edit-or-create-header [{:keys [tr form-mode]}]
  [:h3.font-bold.text-lg.mb-2
   (if (= form-mode :complaint/edit)
     (tr [:code-enforcement/edit-complaint])
     (tr [:code-enforcement/new-complaint]))])


(defn- form-location-title [{:keys [tr address location]}]
  [:div.font-bold.mb-2
   (or address
       (location-address location tr)
       (tr [:code-enforcement/complaint-missing-property]))])


(defn- change-event-fn [event options]
  (when event
    (fn [attr value]
      (cond-> [event attr value]
        options (conj options)))))

(defn- category-selection [{:keys                  [tr lang authority-groups complaint-data
                                                    complaint-category]
                            {immediate :immediate} :change-events}]
  (let [permit-types (->> authority-groups (map :permit-type) distinct sort)
        categories   (<sub [::categories complaint-data authority-groups lang])]
    [:<>
     (when (ff/enabled? :feature-flag.id/complaint-permit-type-selection)
       [:<>
        (when (> (count permit-types) 1)
          [category-filter tr permit-types])
        (when (nil? complaint-category)
          [:div (tr [:code-enforcement/select-category])])])
     [:div.flex.flex-wrap.mt-1.w-full
      (for-frag [category categories
                 :let [selected? (= (:db/id category) complaint-category)]]
        [:button.mr-2.mt-2.last:mr-0
         {:style    {:max-width "320px"}
          :on-click #(do (>evt (immediate :complaint/category-group-config (:db/id category)))
                         (>evt [::set-complaint-category category]))
          :class    [complaint-util/category-base-style
                     (if selected?
                       complaint-util/category-selected-style
                       complaint-util/category-unselected-style)]}
         [:div.flex-1.text-left (config-model/localized-name category lang)]])]]))


(defn- complaint-text-fields [{:keys                       [tr field-prefix form-mode complaint-data]
                               {:keys [immediate delayed]} :change-events}]
  [:div
   {:class-name (when (= form-mode :complaint/public-add) "flex flex-col gap-4 lg:flex-row")}
   [:div.mt-3.flex-1.min-w-0
    [ui/text-area
     (merge {:light      true
             :id         (str field-prefix "-description")
             :invalid    (invalid-value? form-mode :complaint/description true (:complaint/description complaint-data))
             :label-text (tr [:complaint/description])
             :required   true
             :value      (:complaint/description complaint-data)
             :on-change  #(rf/dispatch-sync ((or delayed immediate)
                                             :complaint/description
                                             (futil/tval %)))}
            (when delayed
              {:on-blur #(rf/dispatch (immediate :complaint/description (futil/tval %)))}))]]
   [:div.mt-3.flex-1.min-w-0
    [ui/text-area
     (merge {:light       true
             :id          (str field-prefix "-description-location")
             :label-text  (tr [:complaint/description-of-location])
             :placeholder (tr [:complaint/description-of-location-help])
             :value       (:complaint/description-of-location complaint-data)
             :on-change   #(rf/dispatch-sync ((or delayed immediate)
                                              :complaint/description-of-location
                                              (futil/tval %)))}
            (when delayed
              {:on-blur #(rf/dispatch (immediate :complaint/description-of-location (futil/tval %)))}))]]])


(defn- severity-selection [{:keys                  [tr lang field-prefix complaint-data authority-group
                                                    complaint-severities]
                            {immediate :immediate} :change-events}]
  (when-not (:hide-severity? authority-group)
    (let [severity (:complaint/severity complaint-data)]
      [:div.mt-3
       (into [ui/select-pills {:label-text (tr [:complaint/severity])
                               :value      severity
                               :id         (str field-prefix "-severity-level-selector")
                               :on-change  #(>evt (immediate :complaint/severity %))}]
             (for [level (map :complaint/severity complaint-severities)]
               {:value level
                :text  (tr [level])}))
       (when severity
         [:div.mt-3.text-s.text-gray-800
          (if-let [help-text (-> complaint-severities
                                 (p/find-first #(= severity (:complaint/severity %)))
                                 :complaint-severity-config/help-texts)]
            (-> help-text
                (i18n-model/get-text-from-entities-or-map lang)
                rich-text/html-to-text)
            (tr [(keyword "complaint-severity-help-text" (name severity))]))])])))


(defn- complaint-contact-fields [{:keys                       [tr complaint-data field-prefix form-mode
                                                               required-fields]
                                  {:keys [immediate delayed]} :change-events}]
  [:div
   {:class-name (when (= form-mode :complaint/public-add)
                  "grid grid-cols-1 gap-4 lg:grid-cols-2")}
   (for-frag [attr complaint-model/complaint-contact-fields]
     [:div.min-w-0.mt-2
      [ui/text-input
       (merge {:light      true
               :invalid    (invalid-value?
                             form-mode attr (boolean (required-fields attr)) (get complaint-data attr))
               :id         (str field-prefix "-" (name attr))
               :label-text (tr [attr])
               :required   (boolean (required-fields attr))
               :value      (get complaint-data attr)
               :on-change  #(rf/dispatch-sync ((or delayed immediate) attr (futil/tval %)))
               :type       (case attr
                             :complaint/complainer-phone "tel"
                             :complaint/complainer-email "email"
                             "text")}
              (when delayed
                {:on-blur #(rf/dispatch (immediate attr (futil/tval %)))}))]])])


(defn- external-source-selection [{:keys                  [tr lang field-prefix complaint-data source-selectable?
                                                           complaint-sources]
                                   {immediate :immediate} :change-events}]
  (when source-selectable?
    [:div.mt-2
     (let [source-map    (du/entities->entity-db complaint-sources)
           show-as-list? (< 5 (count complaint-sources))
           id            (str field-prefix "-source-select")
           opts          {:label-text (tr [:complaint/source])
                          :values     complaint-sources
                          :value      (get source-map (:complaint/external-source complaint-data))
                          :val->str   #(complaint-model/complaint-source-label % lang)
                          :on-change  #(>evt (immediate :complaint/external-source (:db/id %)))}]
       (if show-as-list?
         [ui-select/select (merge opts
                                  {:id           id
                                   :allow-empty? false
                                   :light        true
                                   :class-name   "full-width-select"})]
         [ui-radio/radio (merge opts
                                {:name       id
                                 :class-name "flex-wrap"})]))]))


(defn- response-requested-toggle [{:keys                  [tr field-prefix complaint-data]
                                   {immediate :immediate} :change-events}]
  [:div.mt-2.relative ;; Prevent breaking layout and toggle's focus auto-scroll
   (cc/toggle {:toggled    (boolean (:complaint/response-requested? complaint-data))
               :label-text (tr [:complaint/response-requested?])
               :id         (str field-prefix "-response-requested")
               :labelA     (tr [:no])
               :labelB     (tr [:yes])
               :on-toggle  #(>evt (immediate :complaint/response-requested? %))})])


(defn- sign-off-section [{:keys [tr field-prefix]}]
  (when (<sub [::sign-off-required?])
    [:<>
     [:div.border.border-ui-gray.border-solid.px-2.pt-2.mb-4.rounded.mt-2
      [ui-rt/editor-legacy {:initial-value (<sub [::disclaimer])
                            :id            (str (<sub [:language]) "-disclaimer")
                            :label-text    (tr [:complaint-sign-off/disclaimer])}]]
     [cc/checkbox {:id         (str field-prefix "-signed-off?")
                   :label-text (tr [:complaint-sign-off/sign-off])
                   :checked    (boolean (<sub [::signed-off]))
                   :on-change  #(>evt [::toggle-signed-off])}]]))


(defn form-content [{:keys [tr lang authority-groups address location on-change-evt-immediate-save on-change-evt-delayed-save
                            change-event-options field-prefix source-selectable? complaint-data form-mode invalid-reason]}]
  (let [photo-feature-enabled? (ff/enabled? :feature-flag.id/complaint-photos)
        complaint-category     (:complaint/category-group-config complaint-data)
        authority-group        (<sub [::authority-group complaint-data authority-groups])
        complaint-severities   (:complaint-severity-levels authority-group)
        change-events          {:immediate (change-event-fn on-change-evt-immediate-save change-event-options)
                                :delayed   (change-event-fn on-change-evt-delayed-save change-event-options)}]
    [:<>
     [edit-or-create-header (util/mmap tr form-mode)]
     [form-location-title (util/mmap tr address location)]
     [category-selection (util/mmap authority-groups lang complaint-data complaint-category change-events tr)]
     (when authority-group
       (let [required-fields   (-> authority-group :required-complaint-fields set)
             complaint-sources (:complaint-sources authority-group)
             allow-photos?     (-> authority-group :group-features set (contains? :group.feature/complaint-photos))]
         [:<>
          [complaint-text-fields (util/mmap field-prefix form-mode complaint-data change-events tr)]
          [severity-selection (util/mmap lang field-prefix complaint-data authority-group complaint-severities
                                         change-events tr)]
          (when (and photo-feature-enabled?
                     allow-photos?
                     (contains? #{:complaint/add :complaint/public-add} form-mode))
            (if (= form-mode :complaint/public-add)
              [image-attachment-section-v2 tr]
              [image-attachment-section tr]))
          [ui-sep/horizontal-line]
          [complaint-contact-fields (util/mmap complaint-data field-prefix form-mode required-fields change-events tr)]
          [external-source-selection (util/mmap lang field-prefix complaint-data source-selectable? complaint-sources
                                                change-events tr)]
          [response-requested-toggle (util/mmap field-prefix complaint-data change-events tr)]
          [sign-off-section (util/mmap tr field-prefix)]
          (when invalid-reason
            [eb/error-boundary
             [invalid-reason-display tr invalid-reason]])]))]))


(defn form [{:keys [tr lang authority-groups selected-property location municipality-number
                    coordinates source-selectable? on-cancel-click command on-success-evt is-public?]}]
  (let [complaint-data  (<sub [::new-complaint-data])
        signed-off      (<sub [::signed-off])
        authority-group (<sub [::authority-group complaint-data authority-groups])
        cancel-fn       (fn []
                          (>evt [::clear-data])
                          (when on-cancel-click
                            (on-cancel-click)))
        invalid-reason  (<sub [::form-errors complaint-data authority-groups coordinates tr])]
    [:<>
     [:div.flex-grow.overflow-y-auto.px-2.pb-2
      [form-content
       (util/mmap {:on-change-evt-immediate-save ::set-new-complaint-data
                   :field-prefix                 "new"
                   :form-mode                    (if is-public? :complaint/public-add :complaint/add)
                   :address                      (some-> selected-property :address :address/full-address)}
                  tr authority-groups lang source-selectable? complaint-data location
                  invalid-reason)]]
     [:div.flex.justify-end.mt-3
      (cc/button {:size       :small
                  :class-name "mr-2"
                  :kind       :tertiary
                  :on-click   cancel-fn}
                 (tr [:cancel]))
      (if (<sub [::create-in-progress?])
        [:div.mr-2
         (cc/inline-loading {:description (tr [:process.signal.loading/submit])})]
        (cc/button {:size     :small
                    :disabled (boolean invalid-reason)
                    :on-click #(>evt [::submit-new-complaint
                                      command
                                      signed-off
                                      (:authority-group-id authority-group)
                                      municipality-number
                                      selected-property
                                      coordinates
                                      location
                                      complaint-data
                                      (or (:on-success-evt authority-group)
                                          on-success-evt)])}
                   (tr [:code-enforcement/submit])))]]))
